import React, { memo, useRef } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Event } from "../../types/events";
import { SchedulerTheme, ViewConfig, ViewInteractions } from "../../types/views";
import { TimelineResourceRow } from "./timelineRows";
import {
  TIMELINE_EVENT_BORDER_RADIUS,
  TIMELINE_EVENT_CONTINUATION_ARROW_GUTTER,
  TIMELINE_EVENT_CONTINUATION_FADE_WIDTH,
  TIMELINE_EVENT_DRAG_HANDLE_WIDTH,
  TIMELINE_EVENT_TITLE_DRAG_LEFT_PADDING,
  TIMELINE_EVENT_TITLE_LEFT_PADDING,
  TIMELINE_EVENT_TITLE_RIGHT_PADDING,
} from "./timelineConstants";
import { getTimelineEventPosition } from "./timelineEventPosition";
import { useTimelineStickyEventTitle } from "./useTimelineStickyEventTitle";

interface TimelineEventItemProps {
  eventItem: Event;
  resourceId: string | number;
  resourceRowMap: Map<string, TimelineResourceRow>;
  startDate: Date;
  daysToShow: number;
  zoom: number;
  baseDayWidth: number;
  laneIndex?: number;
  laneCount?: number;
  onEventClick?: ViewInteractions["onEventClick"];
  viewConfig?: ViewConfig;
  isDraggable?: boolean;
  schedulerTheme?: SchedulerTheme;
}

const getFallbackHoverColor = (baseColor: string) => {
  const hex = baseColor.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
    return baseColor;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenFactor = 0.8;
  return `rgb(${Math.floor(r * darkenFactor)}, ${Math.floor(
    g * darkenFactor
  )}, ${Math.floor(b * darkenFactor)})`;
};

const TimelineEventItem: React.FC<TimelineEventItemProps> = memo(
  ({
    eventItem,
    resourceId,
    resourceRowMap,
    startDate,
    daysToShow,
    zoom,
    baseDayWidth,
    laneIndex = 0,
    laneCount = 1,
    onEventClick,
    viewConfig,
    isDraggable = false,
    schedulerTheme,
  }) => {
    const resourceRow = resourceRowMap.get(String(resourceId));

    if (!resourceRow) {
      return null;
    }

    const {
      eventStart,
      eventEnd,
      continuesBeforeView,
      canDrag,
      left,
      width,
      top,
      height,
    } = getTimelineEventPosition({
      eventItem,
      resourceRow,
      startDate,
      daysToShow,
      zoom,
      baseDayWidth,
      laneIndex,
      laneCount,
      isDraggable,
    });

    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({
        id: `${eventItem.id}-${resourceId}`,
        data: {
          originalEvent: eventItem,
          type: "event",
          sourceResourceId: resourceId,
        },
      });

    const baseBackgroundColor =
      schedulerTheme?.getEventColor?.(eventItem) || "#2563eb";
    const hoverColor =
      schedulerTheme?.getEventHoverColor?.(eventItem, baseBackgroundColor) ||
      getFallbackHoverColor(baseBackgroundColor);

    const titleLeftPadding = canDrag
      ? TIMELINE_EVENT_TITLE_DRAG_LEFT_PADDING
      : TIMELINE_EVENT_TITLE_LEFT_PADDING;
    const titleRightPadding = TIMELINE_EVENT_TITLE_RIGHT_PADDING;

    const barRef = useRef<HTMLDivElement | null>(null);
    const titleRef = useRef<HTMLDivElement | null>(null);

    useTimelineStickyEventTitle({
      barRef,
      titleRef,
      titleLeftPadding,
      titleRightPadding,
      refreshKey: `${left}:${width}:${eventItem.title}`,
    });

    const eventStyle: React.CSSProperties = {
      position: "absolute",
      left,
      width,
      top,
      height,
      backgroundColor: baseBackgroundColor,
      borderRadius: `${TIMELINE_EVENT_BORDER_RADIUS}px`,
      fontSize: "11px",
      color: schedulerTheme?.eventTextColor || "#ffffff",
      // Use clip-path (not overflow:hidden) for rounded-corner clipping. If we
      // used overflow:hidden here the bar would become its own scroll container
      // and the sticky title below would pin to the bar instead of the timeline
      // body, defeating the purpose.
      clipPath: `inset(0 round ${TIMELINE_EVENT_BORDER_RADIUS}px)`,
      whiteSpace: "nowrap",
      cursor: "default",
      zIndex: isDragging ? 100 : 10 + laneIndex,
      opacity: isDragging ? 0.75 : 1,
      boxShadow: isDragging
        ? "0 4px 8px rgba(0,0,0,0.3)"
        : "0 1px 2px rgba(0,0,0,0.1)",
      transition: isDragging
        ? "none"
        : "background-color 0.2s ease, box-shadow 0.2s ease",
      willChange: isDragging ? "transform" : "auto",
    };

    if (transform) {
      eventStyle.transform = `translate3d(${transform.x}px, ${transform.y}px, 0)`;
    }

    const handleStyle: React.CSSProperties = {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: `${TIMELINE_EVENT_DRAG_HANDLE_WIDTH}px`,
      backgroundColor: "rgba(0,0,0,0.2)",
      cursor: "grab",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderTopLeftRadius: `${TIMELINE_EVENT_BORDER_RADIUS}px`,
      borderBottomLeftRadius: `${TIMELINE_EVENT_BORDER_RADIUS}px`,
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const handleElement = e.currentTarget.querySelector(".drag-handle-dots");

      if (handleElement && handleElement.contains(e.target as Node)) {
        e.stopPropagation();
        return;
      }

      onEventClick?.(eventItem, e.currentTarget, viewConfig);
    };

    const composedBarRef = (node: HTMLDivElement | null) => {
      barRef.current = node;
      setNodeRef(node);
    };

    return (
      <div
        ref={composedBarRef}
        style={eventStyle}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = hoverColor;
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = baseBackgroundColor;
          }
        }}
        onClick={handleClick}
        title={`${eventItem.title}\n${eventStart.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${eventEnd.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`}
      >
        {continuesBeforeView && (
          <>
            <div
              style={{
                position: "absolute",
                left: canDrag ? `${TIMELINE_EVENT_DRAG_HANDLE_WIDTH}px` : "0px",
                top: 0,
                bottom: 0,
                width: `${TIMELINE_EVENT_CONTINUATION_FADE_WIDTH}px`,
                background:
                  "linear-gradient(90deg, rgba(0,0,0,0.35), rgba(0,0,0,0))",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: canDrag
                  ? `${
                      TIMELINE_EVENT_DRAG_HANDLE_WIDTH +
                      TIMELINE_EVENT_CONTINUATION_ARROW_GUTTER
                    }px`
                  : `${TIMELINE_EVENT_CONTINUATION_ARROW_GUTTER}px`,
                top: "50%",
                transform: "translateY(-50%)",
                width: 0,
                height: 0,
                borderTop: "6px solid transparent",
                borderBottom: "6px solid transparent",
                borderRight: "7px solid rgba(255,255,255,0.9)",
                pointerEvents: "none",
              }}
            />
          </>
        )}
        {canDrag && (
          <div
            style={handleStyle}
            {...listeners}
            {...attributes}
            className="drag-handle-dots"
          >
            <svg
              width="6"
              height="10"
              viewBox="0 0 2 4"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ opacity: 0.7 }}
            >
              <circle cx="1" cy="0.5" r="0.5" fill="white" />
              <circle cx="1" cy="1.5" r="0.5" fill="white" />
              <circle cx="1" cy="2.5" r="0.5" fill="white" />
              <circle cx="1" cy="3.5" r="0.5" fill="white" />
            </svg>
          </div>
        )}
        <div
          ref={titleRef}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: titleLeftPadding,
            right: titleRightPadding,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            pointerEvents: "none",
            willChange: "transform",
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {eventItem.title}
          </span>
        </div>
      </div>
    );
  }
);

TimelineEventItem.displayName = "TimelineEventItem";

export default TimelineEventItem;
